import { debounce } from "lodash";
import mapboxgl from "mapbox-gl";
import { useEffect, useMemo, useState } from "react";

import { useGaiaMap } from "./useGaiaMap";

interface GaiaMapBounds {
    boundsWithPadding: mapboxgl.LngLatBounds | null;
    boundsWithoutPadding: mapboxgl.LngLatBounds | null;
}

const getUnpaddedBounds = (mapInstance: mapboxgl.Map): mapboxgl.LngLatBounds => {
    const canvas = mapInstance.getCanvas();
    const { width, height } = canvas;

    // Get corners of the actual visible viewport (no padding)
    const topLeft = mapInstance.unproject([0, 0]);
    const topRight = mapInstance.unproject([width, 0]);
    const bottomRight = mapInstance.unproject([width, height]);
    const bottomLeft = mapInstance.unproject([0, height]);

    // Create bounds from the four corners
    const unpaddedBounds = new mapboxgl.LngLatBounds();
    unpaddedBounds.extend(topLeft);
    unpaddedBounds.extend(topRight);
    unpaddedBounds.extend(bottomRight);
    unpaddedBounds.extend(bottomLeft);

    return unpaddedBounds;
};

export const useGaiaMapBounds = (): GaiaMapBounds => {
    const { map } = useGaiaMap();
    const [boundsWithPadding, setBoundsWithPadding] = useState<mapboxgl.LngLatBounds | null>(null);
    const [boundsWithoutPadding, setBoundsWithoutPadding] = useState<mapboxgl.LngLatBounds | null>(null);

    const debouncedUpdateBounds = useMemo(
        () =>
            debounce(() => {
                if (map) {
                    setBoundsWithPadding(map.getBounds());
                    setBoundsWithoutPadding(getUnpaddedBounds(map));
                }
            }, 300),
        [map],
    );

    useEffect(() => {
        if (!map) return;

        // Set initial bounds immediately
        setBoundsWithPadding(map.getBounds());
        setBoundsWithoutPadding(getUnpaddedBounds(map));

        // Listen for map movement events with debounce
        map.on("moveend", debouncedUpdateBounds);
        map.on("zoomend", debouncedUpdateBounds);

        return () => {
            map.off("moveend", debouncedUpdateBounds);
            map.off("zoomend", debouncedUpdateBounds);
            debouncedUpdateBounds.cancel();
        };
    }, [map, debouncedUpdateBounds]);

    return { boundsWithPadding, boundsWithoutPadding };
};
